import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
    Paper, Typography, Button, TextField, Dialog, DialogActions, 
    DialogContent, DialogTitle, IconButton, MenuItem, Select, FormControl, InputLabel, Box 
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext"; // ✅ Import ThemeContext

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://nugreen.eco";
const API_URL = `${API_BASE_URL}/users`;

const UserManagement = () => {
    const { theme } = useContext(ThemeContext); // ✅ Get theme mode from context
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [openDialog, setOpenDialog] = useState(false);
    const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "partner" });

    const navigate = useNavigate();

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem("user"));

        if (!storedUser || storedUser.role !== "admin") {
            alert("Access Denied: Admins Only");
            navigate("/dashboard");
        } else {
            fetchUsers();
        }
    }, [navigate]);

    // ✅ Fetch users from backend
    const fetchUsers = async () => {
        try {
            const response = await axios.get(API_URL);
            setUsers(response.data);
            setLoading(false);
        } catch (err) {
            setError("Failed to load users");
            setLoading(false);
        }
    };

    // ✅ Delete user from backend
    const handleDeleteUser = async (userId) => {
        if (window.confirm("Are you sure you want to delete this user?")) {
            try {
                await axios.delete(`${API_URL}/${userId}`);
                setUsers(users.filter(user => user.id !== userId)); // Remove user from state
            } catch (err) {
                alert("Failed to delete user");
            }
        }
    };

    // ✅ Add new user to backend (with role selection)
    const handleAddUser = async () => {
        if (!newUser.name || !newUser.email || !newUser.password) {
            alert("All fields are required.");
            return;
        }

        try {
            const response = await axios.post(API_URL, newUser);
            setUsers([...users, response.data]); // Append new user to list
            setOpenDialog(false);
            setNewUser({ name: "", email: "", password: "", role: "partner" }); // Reset form
        } catch (err) {
            alert(err.response?.data?.message || "Failed to add user");
        }
    };

    // ✅ Filter users based on search
    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // ✅ Dynamic Theme Styles
    const backgroundColor = theme === "dark" ? "linear-gradient(135deg, #6e7864, #53574f)" : "linear-gradient(135deg, #d3d7cf, #e4e7e1)";
    const textColor = theme === "dark" ? "#ffffff" : "#1C1C1C";
    const tableBackground = theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "#ffffff";
    const buttonColor = theme === "dark" ? "linear-gradient(135deg, #8aa172, #6e7864)" : "#8aa172";
    const buttonTextColor = theme === "dark" ? "#ffffff" : "#000000";

    return (
        <Box sx={{ padding: "20px", background: backgroundColor, color: textColor, minHeight: "100vh" }}>
            <Typography variant="h5" sx={{ mb: 2, textAlign: "center", fontWeight: "bold", color: textColor }}>
                User Management
            </Typography>

            {/* Search Bar */}
            <TextField
                fullWidth
                variant="outlined"
                label="Search Users"
                sx={{
                    mb: 2,
                    background: tableBackground,
                    borderRadius: "5px",
                    input: { color: textColor },
                    "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#B0B0B0" },
                        "&:hover fieldset": { borderColor: "#A0A0A0" },
                        "&.Mui-focused fieldset": { borderColor: "#909090" },
                    },
                }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
            />

            {/* Add User Button */}
            <Button 
                variant="contained" 
                startIcon={<AddCircleOutlineIcon />} 
                sx={{
                    mb: 2,
                    background: buttonColor,
                    "&:hover": { background: "#6e7864" },
                    color: buttonTextColor,
                }} 
                onClick={() => setOpenDialog(true)}
            >
                Add User
            </Button>

            {loading ? <Typography sx={{ textAlign: "center", color: textColor }}>Loading users...</Typography> : null}
            {error ? <Typography color="error" sx={{ textAlign: "center" }}>{error}</Typography> : null}

            {!loading && !error && (
                <TableContainer component={Paper} sx={{ background: tableBackground, color: textColor }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: textColor }}><b>Name</b></TableCell>
                                <TableCell sx={{ color: textColor }}><b>Email</b></TableCell>
                                <TableCell sx={{ color: textColor }}><b>Role</b></TableCell>
                                <TableCell align="right" sx={{ color: textColor }}><b>Actions</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell sx={{ color: textColor }}>{user.name}</TableCell>
                                    <TableCell sx={{ color: textColor }}>{user.email}</TableCell>
                                    <TableCell sx={{ color: textColor }}>{user.role}</TableCell>
                                    <TableCell align="right">

                <IconButton
                    onClick={() => handleDeleteUser(user.id)}
                    sx={{ color: user.name.toLowerCase() === "test" ? "#ccc" : "#ff6666" }}
                    disabled={user.name.toLowerCase() === "test"}
                >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Add User Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle sx={{ background: tableBackground, color: textColor }}>Add New User</DialogTitle>
                <DialogContent sx={{ background: tableBackground, color: textColor }}>
                    <TextField fullWidth label="Full Name" margin="normal" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
                    <TextField fullWidth label="Email" margin="normal" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
                    <TextField fullWidth type="password" label="Password" margin="normal" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Role</InputLabel>
                        <Select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                            <MenuItem value="partner">Partner</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ background: tableBackground }}>
                    <Button onClick={() => setOpenDialog(false)} sx={{ color: textColor }}>Cancel</Button>
                    <Button onClick={handleAddUser} sx={{ background: buttonColor, color: buttonTextColor }}>Add</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserManagement;
